import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePrices } from "src/context/PricesProvider";
import { useProduct } from "src/context/ProductProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import { Coupon, ProductPrice } from "src/api/types";
import {
  Box,
  Button,
  Divider,
  lighten,
  Stack,
  Typography,
} from "@mui/material";
import Header from "../Headers/Header";
import Product from "../Product";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import Subheader from "../Headers/Subheader";
import { Check } from "@mui/icons-material";
import { theme } from "src/assets/theme";
import PillCounter from "./components/PillCounter";
import PaymentProcessor from "../PaymentProcessor";
import ApplyCoupon from "../ApplyCoupon";
import { useSearchParams } from "react-router-dom";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import { useGetPatientCouponsQuery } from "src/api/services/patientCouponsApi";
import CouponView from "../CouponView";
import ProductsDisplay from "src/components/ProductsDisplay/ProductsDisplay";
import { productInformationArray } from "src/constants/producttInformation";

const formatNumber = (value: number, hasFraction?: boolean) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: hasFraction ? 2 : 0,
  });
};

const EdPaymentInformation = () => {
  const isMobile = useIsMobile();
  const products = usePrices();
  const billingModel = useProduct((p) => p.billing_model);
  const page = "payment-information";
  const { t } = useTranslation();
  const productSlug = useProduct((p) => p.slug);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProduct, setSelectedProduct] = useSelectedProduct();
  const [count, setCount] = useState<number>(
    searchParams.get("quantity") != null
      ? parseInt(searchParams.get("quantity") || "1", 10) /
          (selectedProduct?.number_of_months || 1)
      : 6,
  );
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const informationArray = productInformationArray["ed-ot"];
  const { data: coupons = [] } = useGetPatientCouponsQuery({
    product_slug: productSlug,
    redeemed: false,
  });

  const handleProductChange = useCallback(
    (product: ProductPrice) => {
      setSelectedProduct(product);
    },
    [setSelectedProduct],
  );

  const handleIncrease = useCallback(() => {
    setCount((prev) => prev + 1);
  }, []);
  const handleDecrease = useCallback(() => {
    setCount((prev) => prev - 1);
  }, []);

  const discount = useMemo(() => {
    const appliedDiscount = coupon?.amount ?? 0;

    return coupons.length > 0
      ? coupons.reduce((acc, c) => (acc += c.amount) - appliedDiscount, 0)
      : appliedDiscount;
  }, [coupon?.amount, coupons]);

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return 0;
    }

    const price = Math.max(
      selectedProduct?.amount,
      selectedProduct.amount_limit ?? 0,
    );

    return price * count * selectedProduct.number_of_months - discount;
  }, [count, discount, selectedProduct]);

  const saving = useMemo(() => {
    if (selectedProduct == null) return;
    const defaultPrice =
      selectedProduct.amount * count * selectedProduct.number_of_months;

    const comparedPrice =
      (selectedProduct.competitor_price || 0) *
      count *
      selectedProduct.number_of_months;

    return comparedPrice - defaultPrice + discount;
  }, [count, discount, selectedProduct]);

  const totalPills = useMemo(() => {
    if (selectedProduct == null) return 0;
    return count * selectedProduct?.number_of_months;
  }, [count, selectedProduct]);

  useEffect(() => {
    searchParams.set("quantity", totalPills.toString());

    if (selectedProduct?.stripe_id != null) {
      searchParams.set("price", selectedProduct.stripe_id);
    }

    setSearchParams(searchParams);
  }, [searchParams, selectedProduct?.stripe_id, setSearchParams, totalPills]);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={3}>
          <Header />
          <Product />
          <PillCounter
            count={count}
            increase={handleIncrease}
            decrease={handleDecrease}
          />
        </Stack>
        <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
          <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
            <Subheader />
          </Typography>
          {informationArray.map((i) => (
            <Box key={i} display="flex" gap={1} alignItems="center">
              <Check
                sx={{ color: theme.palette.primary.main }}
                fontSize="medium"
              />
              <Typography variant={isMobile ? "body2" : "body1"}>
                {t(i)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
      <Stack gap={3}>
        <ProductsDisplay
          page={page}
          handleProductChange={handleProductChange}
          products={products}
          selectedProduct={selectedProduct}
          count={count}
        />
        <Stack gap={4}>
          <Stack gap={2}>
            <Stack gap={1}>
              <ApplyCoupon setCoupon={setCoupon} coupon={coupon} />
              {coupons.map((c) => (
                <CouponView coupon={c} />
              ))}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={"22px"} fontWeight={600}>
                  {t(`${page}.total`)}
                </Typography>
                <Typography fontSize={"22px"} fontWeight={600}>
                  $
                  {formatNumber(
                    defaultProductPrice,
                    defaultProductPrice % 1 !== 0,
                  )}
                </Typography>
              </Stack>
              <Typography
                color={theme.palette.primary.main}
                fontStyle="italic"
                fontWeight={600}
                fontSize={"18px"}
              >
                {t(`${page}.${billingModel}.disclaimer`)}
              </Typography>
            </Stack>
            <Button
              variant="text"
              sx={{
                backgroundColor: lighten(theme.palette.secondary.main, 0.3),
                boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                color: "#ffffff",
                fontSize: "16px",
                fontWeight: 700,
                borderRadius: "10px",
                textAlign: "center",
              }}
              onClick={() => handleFocusIntoView("payment-input")}
            >
              {t(`${page}.totalSavings`, {
                saving,
              })}
            </Button>
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>
          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              quantity={totalPills}
              coupon={coupon}
              dueToday={defaultProductPrice}
            />
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default EdPaymentInformation;
